// features/ups/modals/CreateAgencyWizardModal.js
import React, { useMemo, useState } from 'react';
import { X } from 'lucide-react';
import { useDispatch } from 'react-redux';
import { createAgency } from '../../agencies/agenciesSlice';
import { createAccount } from '../../agencyAccounts/agencyAccountsSlice';

export default function CreateAgencyWizardModal({ isOpen, onClose }) {
  const dispatch = useDispatch();

  const [step, setStep] = useState(1);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');

  // Step 1
  const [name, setName] = useState('');
  const [maxActive, setMaxActive] = useState(''); // optional, if you track this

  // Step 2 (optional)
  const [makeLogin, setMakeLogin] = useState(true);
  const [role, setRole] = useState('agency_admin');
  const [username, setUsername] = useState('');
  const [email, setEmail]     = useState('');
  const [password, setPassword] = useState('');

  const canNext = useMemo(() => name.trim().length > 0, [name]);
  const canCreate = useMemo(() => {
    if (!makeLogin) return true;
    return username.trim() && email.trim() && password;
  }, [makeLogin, username, email, password]);

  const reset = () => {
    setStep(1);
    setBusy(false);
    setError('');
    setName(''); setMaxActive('');
    setMakeLogin(true);
    setRole('agency_admin');
    setUsername(''); setEmail(''); setPassword('');
  };

  const handleCreate = async () => {
    if (busy) return;
    setBusy(true);
    setError('');

    try {
      const payload = { name: name.trim() };
      if (maxActive) payload.max_active_drivers = Number(maxActive);

      const agency = await dispatch(createAgency(payload)).unwrap();

      if (makeLogin) {
        await dispatch(createAccount({
          agencyId: agency.id,
          role,
          username: username.trim(),
          email: email.trim(),
          password
        })).unwrap();
      }

      reset();
      onClose();
    } catch (e) {
      setError(typeof e === 'string' ? e : 'Failed to create agency');
    } finally {
      setBusy(false);
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50 bg-black/40 flex items-center justify-center p-4" onClick={onClose}>
      <div className="bg-white rounded-xl w-full max-w-xl shadow-lg" onClick={e => e.stopPropagation()}>
        <div className="flex items-center justify-between p-4 border-b">
          <h3 className="text-lg font-semibold">Create agency</h3>
          <button onClick={onClose} className="p-1 rounded hover:bg-gray-100"><X size={18} /></button>
        </div>

        {/* Stepper */}
        <div className="px-4 pt-4">
          <ol className="flex items-center gap-4 text-sm">
            <li className={`px-2 py-1 rounded ${step >= 1 ? 'bg-blue-600 text-white' : 'bg-gray-200'}`}>1. Agency</li>
            <li className={`px-2 py-1 rounded ${step >= 2 ? 'bg-blue-600 text-white' : 'bg-gray-200'}`}>2. First account</li>
            <li className={`px-2 py-1 rounded ${step >= 3 ? 'bg-blue-600 text-white' : 'bg-gray-200'}`}>3. Review</li>
          </ol>
        </div>

        <div className="p-4 space-y-4">
          {step === 1 && (
            <div className="space-y-4">
              <div>
                <label className="block text-sm mb-1">Agency name<span className="text-red-500">*</span></label>
                <input className="w-full border rounded px-3 py-2" value={name} onChange={e => setName(e.target.value)} />
              </div>
              <div>
                <label className="block text-sm mb-1">Max active drivers (optional)</label>
                <input className="w-full border rounded px-3 py-2" type="number" min="0" value={maxActive} onChange={e => setMaxActive(e.target.value)} />
              </div>
            </div>
          )}

          {step === 2 && (
            <div className="space-y-4">
              <label className="inline-flex items-center gap-2">
                <input type="checkbox" checked={makeLogin} onChange={e => setMakeLogin(e.target.checked)} />
                Also create first login for this agency
              </label>

              {makeLogin && (
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm mb-1">Role</label>
                    <select className="w-full border rounded px-3 py-2" value={role} onChange={e => setRole(e.target.value)}>
                      <option value="agency_admin">Agency Admin</option>
                      <option value="agency_user">Agency User</option>
                    </select>
                  </div>
                  <div>
                    <label className="block text-sm mb-1">Username</label>
                    <input className="w-full border rounded px-3 py-2" value={username} onChange={e => setUsername(e.target.value)} />
                  </div>
                  <div>
                    <label className="block text-sm mb-1">Email</label>
                    <input className="w-full border rounded px-3 py-2" type="email" value={email} onChange={e => setEmail(e.target.value)} />
                  </div>
                  <div>
                    <label className="block text-sm mb-1">Password</label>
                    <input className="w-full border rounded px-3 py-2" type="password" value={password} onChange={e => setPassword(e.target.value)} />
                  </div>
                </div>
              )}
            </div>
          )}

          {step === 3 && (
            <div className="space-y-3 text-sm">
              <div><span className="font-medium">Agency:</span> {name || '—'}</div>
              {maxActive && <div><span className="font-medium">Max active:</span> {maxActive}</div>}
              <div><span className="font-medium">Create first login:</span> {makeLogin ? 'Yes' : 'No'}</div>
              {makeLogin && (
                <>
                  <div><span className="font-medium">Role:</span> {role}</div>
                  <div><span className="font-medium">Username:</span> {username || '—'}</div>
                  <div><span className="font-medium">Email:</span> {email || '—'}</div>
                </>
              )}
            </div>
          )}

          {error && <div className="text-red-600 text-sm">{error}</div>}
        </div>

        <div className="p-4 border-t flex items-center justify-between">
          <button onClick={onClose} className="px-3 py-2 rounded bg-gray-100 hover:bg-gray-200">Cancel</button>
          <div className="flex gap-2">
            {step > 1 && <button onClick={() => setStep(step - 1)} className="px-3 py-2 rounded bg-gray-100 hover:bg-gray-200">Back</button>}
            {step < 3 && (
              <button
                onClick={() => setStep(step + 1)}
                disabled={(step === 1 && !canNext) || (step === 2 && !canCreate)}
                className="px-3 py-2 rounded bg-blue-600 text-white disabled:opacity-60"
              >
                Next
              </button>
            )}
            {step === 3 && (
              <button
                onClick={handleCreate}
                disabled={busy}
                className="px-3 py-2 rounded bg-blue-600 text-white disabled:opacity-60"
              >
                {busy ? 'Creating…' : 'Create'}
              </button>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
